import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Podcast, Newspaper, Download, ExternalLink, FileText, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BlogCard from "@/components/BlogCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getAllPosts } from "@/lib/blog";

type ContentType = "artikel" | "buch" | "podcast" | "newsletter";

interface Publication {
  type: "buch" | "podcast" | "newsletter";
  title: string;
  authors?: string;
  description: string;
  coverImage?: string;
  downloadUrl?: string;
  externalUrl?: string;
  embedHtml?: string;
}

const publications: Publication[] = [
  {
    type: "buch",
    title: "NEW HIRING – Ein neues Bewusstsein im Recruiting",
    authors: "Stephan Rathgeber, Kira Niklas",
    description:
      "2. überarbeitete Auflage mit einem Vorwort von OMR-Gründer Philipp Westermeyer. Warum wir eine Evolution im Recruiting brauchen – und wie Unternehmen sie gestalten können.",
    coverImage: "/books/new-hiring-cover.jpg",
    downloadUrl: "/books/New_Hiring.pdf",
  },
  {
    type: "buch",
    title: "BLUE HIRING – Zeit zum Umdenken",
    authors: "Stephan Rathgeber, Kira Niklas, Dr. Julian Stahl",
    description:
      "Wie man erfolgreich Arbeitskräfte in Industrie, Handel und Dienstleistung gewinnt. Ein umfassender Überblick über die Situation sowie Praxistipps für den Recruitingalltag.",
    coverImage: "/books/blue-hiring-cover.jpg",
    downloadUrl: "/books/Blue_Hiring.pdf",
  },
];

const filters: { label: string; value: ContentType; icon: React.ReactNode }[] = [
  { label: "Artikel", value: "artikel", icon: <FileText className="w-4 h-4" /> },
  { label: "Bücher", value: "buch", icon: <BookOpen className="w-4 h-4" /> },
  { label: "Podcasts", value: "podcast", icon: <Podcast className="w-4 h-4" /> },
  { label: "Newsletter", value: "newsletter", icon: <Newspaper className="w-4 h-4" /> },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.25 } },
};

const Blog = () => {
  const [activeFilter, setActiveFilter] = useState<ContentType>("artikel");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag");
  const allPosts = getAllPosts();
  const posts = activeTag
    ? allPosts.filter((p) => p.tags?.some((t) => t.toLowerCase() === activeTag.toLowerCase()))
    : allPosts;

  const filteredPubs =
    activeFilter === "artikel" ? [] : publications.filter((p) => p.type === activeFilter);

  const hasContent = (type: ContentType) =>
    type === "artikel" ? allPosts.length > 0 : publications.some((p) => p.type === type);

  const clearTag = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("tag");
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <SEOHead
        title="Blog & Veröffentlichungen – Leadership Focus"
        description="Artikel, Bücher, Podcasts und Newsletter von Leadership Focus – Impulse zu Führung, Coaching und Organisationsentwicklung."
        canonical="https://leadership-focus.de/blog"
      />
      <Header />
      <main className="container py-20 min-h-[60vh]">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="mb-12"
        >
          <motion.p variants={fadeInUp} className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
            Blog &amp; Veröffentlichungen
          </motion.p>
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Impulse aus unserer Arbeit
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl">
            Artikel, Bücher, Podcasts und Newsletter zu Führung, Coaching und Organisationsentwicklung.
          </motion.p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-10">
          {filters.map((f) => (
            <Button
              key={f.value}
              variant={activeFilter === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(f.value)}
              className="gap-1.5"
              disabled={!hasContent(f.value)}
            >
              {f.icon}
              {f.label}
            </Button>
          ))}
        </div>

        {activeTag && activeFilter === "artikel" && (
          <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
            <span>Gefiltert nach Tag:</span>
            <button
              onClick={clearTag}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              #{activeTag}
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {activeFilter === "artikel" ? (
            <motion.div
              key="artikel"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {posts.length === 0 ? (
                <motion.p variants={fadeInUp} className="text-muted-foreground col-span-full text-center py-16">
                  {activeTag ? `Keine Artikel mit Tag „${activeTag}" gefunden.` : "Artikel folgen in Kürze."}
                </motion.p>
              ) : (
                posts.map((p) => <BlogCard key={p.slug} post={p} />)
              )}
            </motion.div>
          ) : (
            <motion.div
              key={activeFilter}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredPubs.length === 0 ? (
                <motion.p variants={fadeInUp} className="text-muted-foreground col-span-full text-center py-16">
                  Inhalte folgen in Kürze.
                </motion.p>
              ) : (
                filteredPubs.map((pub, i) => <PublicationCard key={`${pub.type}-${i}`} pub={pub} />)
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </>
  );
};

function PublicationCard({ pub }: { pub: Publication }) {
  const typeLabel = pub.type === "buch" ? "Buch" : pub.type === "podcast" ? "Podcast" : "Newsletter";
  const TypeIcon = pub.type === "buch" ? BookOpen : pub.type === "podcast" ? Podcast : Newspaper;

  return (
    <motion.div variants={fadeInUp}>
      <Card className="overflow-hidden h-full flex flex-col group hover:shadow-lg transition-shadow">
        {pub.coverImage && (
          <div className="aspect-[3/4] overflow-hidden bg-muted">
            <img
              src={pub.coverImage}
              alt={pub.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        )}
        {pub.embedHtml && <div className="px-4 pt-4" dangerouslySetInnerHTML={{ __html: pub.embedHtml }} />}
        <CardContent className="flex-1 flex flex-col p-6">
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary mb-2 w-fit">
            <TypeIcon className="w-3.5 h-3.5" />
            {typeLabel}
          </span>
          <h3 className="text-lg font-serif font-semibold text-foreground mb-1 leading-snug">{pub.title}</h3>
          {pub.authors && <p className="text-sm text-muted-foreground mb-2">{pub.authors}</p>}
          <p className="text-sm text-muted-foreground mb-4 flex-1">{pub.description}</p>
          <div className="flex gap-2 mt-auto">
            {pub.downloadUrl && (
              <Button asChild size="sm" variant="default" className="gap-1.5">
                <a href={pub.downloadUrl} download>
                  <Download className="w-4 h-4" />
                  PDF Download
                </a>
              </Button>
            )}
            {pub.externalUrl && (
              <Button asChild size="sm" variant="outline" className="gap-1.5">
                <a href={pub.externalUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                  Öffnen
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default Blog;
